import { useEffect, useCallback, useState, useRef, memo } from 'react';
import {
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  IconButton,
  TextField,
  InputAdornment,
  Button,
  Chip,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import CloseIcon from '@mui/icons-material/Close';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import SortIcon from '@mui/icons-material/Sort';
import { useFavoritesStore } from '../../stores/favoritesStore';
import { usePlayerStore } from '../../stores/playerStore';

const FavoritesList = () => {
  // Ref for search input focus
  const searchInputRef = useRef<HTMLInputElement>(null);
  
  const {
    favorites,
    sortMode,
    loadFavorites,
    removeFromFavorites,
    toggleSortMode,
    setSearchText,
    getFilteredFavorites,
  } = useFavoritesStore();

  const { setQueueAndBeginPlaying } = usePlayerStore();

  const [showSearch, setShowSearch] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  // Focus search input when it appears
  useEffect(() => {
    if (showSearch && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [showSearch]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  const handleSearchChange = useCallback((value: string) => {
    setLocalSearch(value);
    if (debounceTimer.current) {
      clearTimeout(debounceTimer.current);
    }
    debounceTimer.current = setTimeout(() => {
      setSearchText(value);
    }, 300);
  }, [setSearchText]);

  const handlePlayFavorite = useCallback((index: number) => {
    const filtered = getFilteredFavorites();
    const item = filtered[index];
    if (item) {
      setQueueAndBeginPlaying([item]);
    }
  }, [getFilteredFavorites, setQueueAndBeginPlaying]);

  const handlePlayAll = useCallback(() => {
    const filtered = getFilteredFavorites();
    if (filtered.length > 0) {
      setQueueAndBeginPlaying(filtered);
    }
  }, [getFilteredFavorites, setQueueAndBeginPlaying]);

  const handleRemove = useCallback((e: React.MouseEvent, index: number) => {
    e.stopPropagation();
    // Find the actual index in the full favorites array
    const filtered = getFilteredFavorites();
    const item = filtered[index];
    const realIndex = favorites.findIndex(f => f.id === item.id);
    if (realIndex >= 0) {
      removeFromFavorites(realIndex);
    }
  }, [favorites, getFilteredFavorites, removeFromFavorites]);

  const filtered = getFilteredFavorites();

  return (
    <Box sx={{
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
    }}>
      {/* Header */}
      <Box sx={{
        p: 2,
        borderBottom: '1px solid',
        borderColor: 'divider',
        bgcolor: 'background.paper',
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 500, fontSize: '1.125rem' }}>
            Favorites ({favorites.length})
          </Typography>
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <IconButton 
              onClick={toggleSortMode} 
              size="small" 
              title={`Sort by ${sortMode === 'artist' ? 'title' : 'artist'}`}
            >
              <SortIcon fontSize="small" />
            </IconButton>
            <IconButton onClick={() => setShowSearch(!showSearch)} size="small">
              {showSearch ? <CloseIcon fontSize="small" /> : <SearchIcon fontSize="small" />}
            </IconButton>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
          <Chip
            label={`Sorted by ${sortMode}`}
            size="small"
            variant="outlined"
            sx={{ 
              height: 32,
              fontSize: '0.875rem',
            }}
          />

          {favorites.length > 0 && (
            <Button
              variant="outlined"
              size="small"
              startIcon={<PlayArrowIcon fontSize="small" />}
              onClick={handlePlayAll}
              sx={{ 
                textTransform: 'none',
                borderRadius: 2,
                height: 32,
              }}
            >
              Play All ({filtered.length})
            </Button>
          )}
        </Box>

        {showSearch && (
          <TextField
            fullWidth
            variant="outlined"
            placeholder="Search favorites..."
            value={localSearch}
            onChange={(e) => handleSearchChange(e.target.value)}
            size="small"
            inputRef={searchInputRef}
            sx={{ 
              mt: 2,
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" fontSize="small" />
                </InputAdornment>
              ),
              endAdornment: localSearch && (
                <InputAdornment position="end">
                  <IconButton onClick={() => handleSearchChange('')} size="small">
                    <ClearIcon fontSize="small" />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        )}
      </Box>

      {/* Favorites List */}
      <Box sx={{ flex: 1, overflow: 'auto', p: 1 }}>
        {filtered.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 6 }}>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {favorites.length === 0 ? 'No favorites yet' : 'No results found'}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              {favorites.length === 0
                ? 'Use the heart button on the player to save songs'
                : 'Try a different search term'}
            </Typography>
          </Box>
        ) : (
          <List sx={{ py: 0 }}>
            {filtered.map((item, index) => (
              <ListItemButton
                key={`${item.id}-${index}`}
                onClick={() => handlePlayFavorite(index)}
                sx={{
                  borderRadius: 2,
                  mb: 0.5,
                  py: 1,
                  px: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  border: '1px solid',
                  borderColor: 'transparent',
                  '&:hover': {
                    bgcolor: 'action.hover',
                    borderColor: 'divider',
                  },
                }}
              >
                <ListItemText
                  sx={{ flex: 1, minWidth: 0 }}
                  primary={
                    <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                      {item.title}
                    </Typography>
                  }
                  secondary={
                    <Typography 
                      variant="caption" 
                      noWrap 
                      color="text.secondary"
                      sx={{
                        display: 'block',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {item.artist} {item.album ? `• ${item.album}` : ''}
                    </Typography>
                  }
                />
                <IconButton
                  edge="end"
                  size="small"
                  onClick={(e) => handleRemove(e, index)}
                  sx={{ 
                    opacity: 0.6, 
                    '&:hover': { 
                      opacity: 1, 
                      color: 'error.main',
                      bgcolor: 'rgba(239, 83, 80, 0.08)',
                    } 
                  }}
                >
                  <DeleteIcon fontSize="small" />
                </IconButton>
              </ListItemButton>
            ))}
          </List>
        )}
      </Box>
    </Box>
  );
};

export default memo(FavoritesList);
